import React, { useState, useEffect } from 'react';
import { Eye, FileText } from 'lucide-react';
import { API_URL } from '@/api/constant';
import { StatusBadge } from './StatusBadge';
import ViewDocumentModal, { DocumentItem } from './ViewDocumentModal';

interface MediaItem {
    url: string;
    [key: string]: any;
}

interface DetailsGalleryProps {
    media: MediaItem[];
    name: string;
    status?: string;
    className?: string;
}

type DocKind = 'pdf' | 'doc' | 'txt' | 'image';

const getDocKind = (url: string): DocKind => {
    if (!url) return 'image';
    const clean = url.split('?')[0].split('#')[0].toLowerCase();
    const ext = clean.split('.').pop() || '';
    if (ext === 'pdf' || clean.includes('/pdf')) return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'doc';
    if (['txt', 'rtf'].includes(ext)) return 'txt';
    return 'image';
};

export const DetailsGallery: React.FC<DetailsGalleryProps> = ({
    media,
    name,
    status,
    className = ''
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);

    useEffect(() => {
        if (media && media.length > 0) {
            setActiveIndex(0);
        }
    }, [media]);

    if (!media || media.length === 0) {
        return (
            <div className={`bg-card rounded-2xl border shadow-sm p-4 ${className}`}>
                <div className="aspect-square rounded-xl overflow-hidden bg-secondary/10 border border-secondary/20 flex items-center justify-center text-muted-foreground">
                    No Document Available
                </div>
            </div>
        );
    }

    // Build DocumentItem list for the modal
    const docItems: DocumentItem[] = media.map((img, i) => ({
        id: img.id ?? String(i),
        url: img.url.startsWith('http') || img.url.startsWith('blob:') ? img.url : `${API_URL}/${img.url}`,
        label: `${name} — Document ${i + 1}`,
        subtitle: img.referenceType ?? img.type ?? undefined,
        uploadedAt: img.createdAt ?? undefined,
    }));

    const activeUrl = docItems[activeIndex]?.url ?? '';
    const activeDocKind = getDocKind(activeUrl);

    const openModal = (idx: number) => {
        setModalIndex(idx);
        setModalOpen(true);
    };

    return (
        <div className={`bg-card rounded-2xl border shadow-sm p-4 ${className}`}>
            {/* ── Main Document / Image Preview ── */}
            <div
                className="aspect-square rounded-2xl overflow-hidden bg-background border border-border/60 mb-4 group relative cursor-pointer shadow-sm hover:shadow-md transition-all duration-300"
                onClick={() => openModal(activeIndex)}
                title="Click to view document"
            >
                {activeDocKind === 'pdf' ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-500/5 via-red-500/10 to-red-500/5 text-red-600 transition-all duration-300 group-hover:from-red-500/10 group-hover:to-red-500/15">
                        <div className="p-3.5 rounded-2xl bg-white shadow-md border border-red-100 group-hover:scale-110 transition-transform duration-300">
                            <FileText className="w-9 h-9 text-red-500" />
                        </div>
                        <div className="text-center mt-3 space-y-1 w-full px-2">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-red-500/10 text-[9px] font-extrabold text-red-600 uppercase tracking-widest border border-red-500/20 shadow-2xs">
                                PDF Document
                            </span>
                            <p className="text-[11px] font-semibold text-foreground/80 truncate w-full px-1">
                                Click to View
                            </p>
                        </div>
                    </div>
                ) : activeDocKind === 'doc' ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-blue-500/5 via-blue-500/10 to-blue-500/5 text-blue-600 transition-all duration-300 group-hover:from-blue-500/10 group-hover:to-blue-500/15">
                        <div className="p-3.5 rounded-2xl bg-white shadow-md border border-blue-100 group-hover:scale-110 transition-transform duration-300">
                            <FileText className="w-9 h-9 text-blue-500" />
                        </div>
                        <div className="text-center mt-3 space-y-1 w-full px-2">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-500/10 text-[9px] font-extrabold text-blue-600 uppercase tracking-widest border border-blue-500/20 shadow-2xs">
                                Word Document
                            </span>
                            <p className="text-[11px] font-semibold text-foreground/80 truncate w-full px-1">
                                Click to View
                            </p>
                        </div>
                    </div>
                ) : activeDocKind === 'txt' ? (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-amber-500/5 via-amber-500/10 to-amber-500/5 text-amber-600 transition-all duration-300 group-hover:from-amber-500/10 group-hover:to-amber-500/15">
                        <div className="p-3.5 rounded-2xl bg-white shadow-md border border-amber-100 group-hover:scale-110 transition-transform duration-300">
                            <FileText className="w-9 h-9 text-amber-500" />
                        </div>
                        <div className="text-center mt-3 space-y-1 w-full px-2">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/10 text-[9px] font-extrabold text-amber-600 uppercase tracking-widest border border-amber-500/20 shadow-2xs">
                                Text File
                            </span>
                            <p className="text-[11px] font-semibold text-foreground/80 truncate w-full px-1">
                                Click to View
                            </p>
                        </div>
                    </div>
                ) : (
                    <img
                        src={activeUrl}
                        alt={name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://placehold.co/600x600?text=No+Image';
                        }}
                    />
                )}

                {/* Hover overlay — eye icon */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3 shadow-lg">
                        <Eye className="w-6 h-6 text-primary" />
                    </div>
                </div>

                {/* Status badge */}
                {status && (
                    <div className="absolute top-3 right-3 z-10">
                        <StatusBadge status={status} />
                    </div>
                )}
            </div>

            {/* ── Thumbnails ── */}
            {media.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                    {docItems.map((doc, i) => {
                        const itemDocKind = getDocKind(doc.url);
                        return (
                            <button
                                key={doc.id}
                                onClick={() => setActiveIndex(i)}
                                onDoubleClick={() => openModal(i)}
                                className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${activeIndex === i
                                    ? 'border-primary shadow-md'
                                    : 'border-transparent hover:border-secondary-foreground/20 opacity-70 hover:opacity-100'
                                    }`}
                                title="Click to select · Double-click to view full"
                            >
                                {itemDocKind === 'pdf' ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-red-500/10 text-red-500 p-1">
                                        <FileText className="w-5 h-5 text-red-500" />
                                        <span className="text-[8px] font-extrabold uppercase tracking-wider text-red-600">PDF</span>
                                    </div>
                                ) : itemDocKind === 'doc' ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-blue-500/10 text-blue-500 p-1">
                                        <FileText className="w-5 h-5 text-blue-500" />
                                        <span className="text-[8px] font-extrabold uppercase tracking-wider text-blue-600">DOC</span>
                                    </div>
                                ) : itemDocKind === 'txt' ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-amber-500/10 text-amber-500 p-1">
                                        <FileText className="w-5 h-5 text-amber-500" />
                                        <span className="text-[8px] font-extrabold uppercase tracking-wider text-amber-600">TXT</span>
                                    </div>
                                ) : (
                                    <img
                                        src={doc.url}
                                        alt={doc.label}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://placehold.co/100x100?text=Error';
                                        }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* ── View Document Modal ── */}
            <ViewDocumentModal
                open={modalOpen}
                onOpenChange={setModalOpen}
                documents={docItems}
                initialIndex={modalIndex}
            />
        </div>
    );
};
